// Client error reporter (25 Sep 2026). Runs before hydration (Next instrumentation-client).
// Production React reports hydration errors (#418 etc.) with no component stack, so for those it also sends WHERE the
// server render and the client render diverge: after React recovers, the live DOM is the client render; the page's
// server HTML is re-fetched once, React's streaming swaps ($RC boundaries, $RS segments) are applied to it so it is the
// final server DOM, and both are compared element by element (tag, id, classes, own text) in document order.
// Emails/phones masked, query strings dropped, at most 3 reports per page view. Receiver: /api/client-error (pm2 log).

// Body as parsed, BEFORE React hydrates (this module runs first). Compared with the client render on a hydration error,
// it shows what React found vs what it expected -- including anything that changed the DOM before hydration.
const PRE_HYDRATION: string | null = typeof document !== "undefined" && document.body ? document.body.innerHTML : null

const HYDRATION = /Minified React error #(418|419|422|423|425)\b|Hydration failed|did not match/i
const MAX_REPORTS = 3
let sent = 0

const mask = (s: string) =>
  s.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]").replace(/(\+?61|\b0)[\d ]{8,12}\d/g, "[phone]")

// ---- server DOM as the browser ends up with it (React streaming swaps applied) ----
function finalServerDoc(html: string): Document {
  const doc = new DOMParser().parseFromString(html, "text/html")
  for (const [, kind, a, b] of Array.from(html.matchAll(/\$R([CS])\("([^"]+)","([^"]+)"\)/g))) {
    if (kind === "S") {
      // $RS(segment, placeholder): move the segment's children in place of the placeholder
      const seg = doc.getElementById(a), ph = doc.getElementById(b)
      if (!seg || !ph || !ph.parentNode) continue
      seg.remove()
      while (seg.firstChild) ph.parentNode.insertBefore(seg.firstChild, ph)
      ph.remove()
    } else {
      // $RC(boundary template, content): drop the fallback up to the matching <!--/$-->, insert the content there
      const content = doc.getElementById(b), tpl = doc.getElementById(a)
      if (!content || !tpl) continue
      content.remove()
      const marker = tpl.previousSibling, parent = tpl.parentNode
      if (!marker || !parent) continue
      let node: ChildNode | null = marker.nextSibling, depth = 0
      while (node) {
        if (node.nodeType === 8) {
          const d = (node as Comment).data
          if (d === "/$") { if (depth === 0) break; depth-- } else if (d === "$" || d === "$?" || d === "$!" || d === "$~") depth++
        }
        const next: ChildNode | null = node.nextSibling
        parent.removeChild(node)
        node = next
      }
      while (content.firstChild) parent.insertBefore(content.firstChild, node)
      ;(marker as Comment).data = "$"
    }
  }
  return doc
}

// ---- element signatures in document order ----
const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEMPLATE", "LINK", "META", "NEXT-ROUTE-ANNOUNCER", "IFRAME"])
type Sig = { sig: string; el: Element }
function signatures(root: Element | null): Sig[] {
  const out: Sig[] = []
  const walk = (el: Element) => {
    if (SKIP.has(el.tagName.toUpperCase())) return
    if (el.id && /^[SPB]:\d+$/.test(el.id)) return // leftover streaming containers
    if (el.getAttribute("aria-hidden") === "true" && /(^|\s)-top-1(\s|$)/.test(el.getAttribute("class") || "")) return // chat unread badge (set after hydration)
    let own = ""
    el.childNodes.forEach((n) => { if (n.nodeType === 3) own += n.textContent })
    own = own.replace(/\s+/g, " ").trim().slice(0, 40)
    const cls = (el.getAttribute("class") || "").split(/\s+/).filter(Boolean).slice(0, 2).join(".")
    // React useId ids (_R_x_ server / _r_x_ client) always differ after a recovery re-render: normalise them
    const id = el.id ? el.id.replace(/_[Rr]_[0-9a-z]+_/g, "_id_") : ""
    out.push({ sig: `${el.tagName.toLowerCase()}${id ? "#" + id : ""}${cls ? "." + cls : ""}${own ? "|" + own : ""}`, el })
    for (const c of Array.from(el.children)) walk(c)
  }
  if (root) walk(root)
  return out
}
const path = (el: Element | null) => {
  const parts: string[] = []
  for (let e = el, i = 0; e && e.tagName !== "BODY" && i < 5; e = e.parentElement, i++)
    parts.unshift(e.tagName.toLowerCase() + (e.id ? "#" + e.id : "") + (e.getAttribute("class") ? "." + e.getAttribute("class")!.split(/\s+/)[0] : ""))
  return parts.join(" > ")
}
const snippet = (el?: Element) => (el ? mask(el.outerHTML.replace(/\s+/g, " ")).slice(0, 220) : "(none)")

function divergences(server: Sig[], client: Sig[], max = 4) {
  const out: Record<string, string>[] = []
  let i = 0, j = 0
  while (i < server.length && j < client.length && out.length < max) {
    if (server[i].sig === client[j].sig) { i++; j++; continue }
    out.push({ at: path(server[i].el), server: mask(server[i].sig), client: mask(client[j].sig), server_html: snippet(server[i].el), client_html: snippet(client[j].el) })
    // resync: whichever side finds the other's current element soonest
    let di = -1, dj = -1
    for (let k = 1; k <= 40 && di < 0 && i + k < server.length; k++) if (server[i + k].sig === client[j].sig) di = k
    for (let k = 1; k <= 40 && dj < 0 && j + k < client.length; k++) if (client[j + k].sig === server[i].sig) dj = k
    if (di < 0 && dj < 0) { i++; j++ } else if (dj < 0 || (di >= 0 && di <= dj)) i += di; else j += dj
  }
  return out
}

async function hydrationDiff() {
  await new Promise((r) => setTimeout(r, 2500)) // let React finish client-rendering and pending reveals settle
  const res = await fetch(location.pathname + location.search, { credentials: "include", cache: "no-store" })
  const server = signatures(finalServerDoc(await res.text()).body)
  const client = signatures(document.body)
  const out: Record<string, unknown> = { divergences: divergences(server, client), counts: `${server.length}/${client.length}` }
  if (PRE_HYDRATION != null) {
    const pre = signatures(new DOMParser().parseFromString(`<body>${PRE_HYDRATION}</body>`, "text/html").body)
    out.pre_vs_client = divergences(pre, client)
    out.pre_vs_server = divergences(server, pre)
    out.pre_count = pre.length
  }
  return out
}

async function report(kind: string, message: string, stack?: string) {
  if (sent >= MAX_REPORTS) return
  sent++
  const hydration = HYDRATION.test(message)
  const payload: Record<string, unknown> = {
    kind: hydration ? "hydration" : kind,
    path: location.pathname,
    message: mask(message).slice(0, 400),
    stack: stack ? mask(stack).slice(0, 1200) : undefined,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ms_since_load: Math.round(performance.now()),
  }
  if (hydration) {
    try { Object.assign(payload, await hydrationDiff()) } catch (e) { payload.diff_error = String(e).slice(0, 200) }
  }
  const body = JSON.stringify(payload)
  try {
    if (!navigator.sendBeacon?.("/api/client-error", new Blob([body], { type: "application/json" })))
      await fetch("/api/client-error", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true })
  } catch {}
}

// A server action (POST with a Next-Action header) whose fetch rejects (network dropped) fails with a readable
// message instead of the browser's bare "Failed to fetch" (25 Sep 2026, BOO 3840c13/150915e parity). No retry here:
// only the idempotent checkout steps retry, explicitly (src/lib/util/retry-network.ts); add-to-cart never does.
if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const origFetch = window.fetch.bind(window)
  const isAction = (init?: RequestInit) => {
    const h = init?.headers
    if (!h || (init?.method || "GET").toUpperCase() !== "POST") return false
    if (h instanceof Headers) return h.has("next-action")
    if (Array.isArray(h)) return h.some(([k]) => k.toLowerCase() === "next-action")
    return Object.keys(h).some((k) => k.toLowerCase() === "next-action")
  }
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      return await origFetch(input, init)
    } catch (err) {
      if (!isAction(init) || !(err instanceof TypeError) || init?.signal?.aborted) throw err
      throw new TypeError("We couldn't reach the store. Check your connection and try again.")
    }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    const err = (e as ErrorEvent).error
    void report("error", String(err?.message ?? (e as ErrorEvent).message ?? "error"), err?.stack)
  })
  window.addEventListener("unhandledrejection", (e) => {
    const r = (e as PromiseRejectionEvent).reason
    const msg = String(r?.message ?? r ?? "unhandled rejection")
    if (/Failed to fetch|NetworkError|Load failed|aborted|couldn.t reach the store/i.test(msg)) return // network noise, not app bugs
    void report("unhandledrejection", msg, r?.stack)
  })
}

export {}
